import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Image as ImageIcon, Trash2, Plus, Save, Settings, LayoutGrid, Star } from "lucide-react";
import { getSettings, updateSettings, fetchCourtsAdmin, updateCourt, uploadImage, resolveImg, getFeaturedPartner, updateFeaturedPartner } from "../adminApi";
import { useToast } from "../../hooks/use-toast";
import { Switch } from "../../components/ui/switch";

/* ---------- Reusable image upload field ---------- */
export function ImageField({ label, hint, value, onChange, aspect = "h-24 w-40" }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast({ title: "Image too large", description: "Max 6MB." }); return; }
    setBusy(true);
    try { const { url } = await uploadImage(file); onChange(url); }
    catch { toast({ title: "Upload failed", description: "Try again or paste a URL." }); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const showUrl = typeof value === "string" && !value.startsWith("/api/") && !value.startsWith("data:");
  return (
    <div>
      {label && <label className="lbl">{label}{hint && <span className="text-white/40"> {hint}</span>}</label>}
      <div className="flex items-start gap-4">
        <div className={`${aspect} rounded-lg overflow-hidden bg-[#06170f] border border-white/10 shrink-0 flex items-center justify-center`}>
          {value ? <img src={resolveImg(value)} alt="preview" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-white/20" />}
        </div>
        <div className="flex-1 space-y-2">
          <input value={showUrl ? value : ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste image URL…" className="adm-input" />
          <div className="flex items-center gap-2">
            <label className="cursor-pointer inline-flex items-center gap-2 bg-[#06170f] border border-white/15 hover:border-sc-lime rounded-lg px-4 py-2 text-white/80 font-grotesk text-sm transition-colors">
              {busy ? <Loader2 className="w-4 h-4 text-sc-lime animate-spin" /> : <ImageIcon className="w-4 h-4 text-sc-lime" />} {busy ? "Uploading…" : "Upload"}
              <input type="file" accept="image/*" onChange={pick} className="hidden" />
            </label>
            {value && <button type="button" onClick={() => onChange(null)} className="text-white/40 hover:text-red-400 inline-flex items-center gap-1 text-sm font-grotesk"><Trash2 className="w-4 h-4" /> Remove</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Gallery (list of images) ---------- */
function GalleryField({ label, value = [], onChange }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) {
        if (f.size > 6 * 1024 * 1024) { toast({ title: `${f.name} too large`, description: "Max 6MB." }); continue; }
        const { url } = await uploadImage(f);
        urls.push(url);
      }
      onChange([...(value || []), ...urls]);
    } catch { toast({ title: "Upload failed" }); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div>
      <label className="lbl">{label}<span className="text-white/40"> ({value.length} photos)</span></label>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {value.map((src, i) => (
          <div key={i} className="relative group rounded-lg overflow-hidden border border-white/10 aspect-[4/3] bg-[#06170f]">
            <img src={resolveImg(src)} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button type="button" onClick={() => move(i, -1)} className="w-8 h-8 rounded-full bg-white/15 text-white hover:bg-sc-lime hover:text-[#06170f] text-xs">←</button>
              <button type="button" onClick={() => removeAt(i)} className="w-8 h-8 rounded-full bg-white/15 text-white hover:bg-red-500"><Trash2 className="w-4 h-4 mx-auto" /></button>
              <button type="button" onClick={() => move(i, 1)} className="w-8 h-8 rounded-full bg-white/15 text-white hover:bg-sc-lime hover:text-[#06170f] text-xs">→</button>
            </div>
          </div>
        ))}
        <label className="cursor-pointer aspect-[4/3] rounded-lg border-2 border-dashed border-white/15 hover:border-sc-lime flex flex-col items-center justify-center text-white/50 hover:text-sc-lime transition-colors">
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Plus className="w-6 h-6" /><span className="text-xs font-grotesk mt-1">Add photos</span></>}
          <input type="file" accept="image/*" multiple onChange={add} className="hidden" />
        </label>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-[#0b241a] border border-white/10 rounded-2xl p-6 md:p-7 space-y-5">
    <h3 className="font-display uppercase text-xl text-sc-lime">{title}</h3>
    {children}
  </div>
);
const Field = ({ label, children }) => (<div><label className="lbl">{label}</label>{children}</div>);

/* ---------- Site Settings Panel ---------- */
export function SettingsPanel({ onLogout }) {
  const { toast } = useToast();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getSettings().then((d) => setS(d && d.hero ? d : { hero: {}, contact: {}, hours: [], about: {} })).catch(() => onLogout && onLogout()); }, [onLogout]);

  if (!s) return <div className="text-white/50 flex items-center gap-2 font-grotesk"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  const hero = s.hero || {}, contact = s.contact || {}, about = s.about || {}, hours = s.hours || [], banners = s.banners || {};
  const setHero = (k, v) => setS((p) => ({ ...p, hero: { ...p.hero, [k]: v } }));
  const setContact = (k, v) => setS((p) => ({ ...p, contact: { ...p.contact, [k]: v } }));
  const setAbout = (k, v) => setS((p) => ({ ...p, about: { ...p.about, [k]: v } }));
  const setBanner = (k, v) => setS((p) => ({ ...p, banners: { ...p.banners, [k]: v } }));
  const setHour = (i, v) => setS((p) => ({ ...p, hours: p.hours.map((h, idx) => idx === i ? { ...h, time: v } : h) }));

  const save = async () => {
    setSaving(true);
    try { await updateSettings(s); toast({ title: "Saved", description: "Site settings updated." }); }
    catch { toast({ title: "Couldn't save", description: "Please try again." }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <Section title="Hero — Headline & Graphic">
        <ImageField label="Headline Hero Graphic" hint="(full-screen background)" value={hero.image} onChange={(v) => setHero("image", v)} aspect="h-24 w-40" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Headline line 1"><input value={hero.line1 || ""} onChange={(e) => setHero("line1", e.target.value)} className="adm-input" /></Field>
          <Field label="Headline line 2 (accent)"><input value={hero.line2 || ""} onChange={(e) => setHero("line2", e.target.value)} className="adm-input" /></Field>
        </div>
        <Field label="Badge text"><input value={hero.badge || ""} onChange={(e) => setHero("badge", e.target.value)} className="adm-input" /></Field>
        <Field label="Subtext"><textarea rows={3} value={hero.subtext || ""} onChange={(e) => setHero("subtext", e.target.value)} className="adm-input resize-none" /></Field>
      </Section>

      <Section title="Contact Details">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Phone (display)"><input value={contact.phone || ""} onChange={(e) => setContact("phone", e.target.value)} className="adm-input" /></Field>
          <Field label="Phone (raw, for links)"><input value={contact.phoneRaw || ""} onChange={(e) => setContact("phoneRaw", e.target.value)} className="adm-input" placeholder="+639209615610" /></Field>
        </div>
        <Field label="Email"><input value={contact.email || ""} onChange={(e) => setContact("email", e.target.value)} className="adm-input" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Address line 1"><input value={contact.addressLine1 || ""} onChange={(e) => setContact("addressLine1", e.target.value)} className="adm-input" /></Field>
          <Field label="Address line 2"><input value={contact.addressLine2 || ""} onChange={(e) => setContact("addressLine2", e.target.value)} className="adm-input" /></Field>
        </div>
        <Field label="Map search query"><input value={contact.mapQuery || ""} onChange={(e) => setContact("mapQuery", e.target.value)} className="adm-input" /></Field>
      </Section>

      <Section title="Open Play Hours">
        <div className="grid sm:grid-cols-2 gap-3">
          {hours.map((h, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="font-grotesk text-white/70 w-24 shrink-0 text-sm">{h.day}</span>
              <input value={h.time || ""} onChange={(e) => setHour(i, e.target.value)} className="adm-input" placeholder="5am – 9pm" />
            </div>
          ))}
        </div>
      </Section>

      <Section title="About Page">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Eyebrow"><input value={about.eyebrow || ""} onChange={(e) => setAbout("eyebrow", e.target.value)} className="adm-input" /></Field>
          <Field label="Title"><input value={about.title || ""} onChange={(e) => setAbout("title", e.target.value)} className="adm-input" /></Field>
        </div>
        <ImageField label="About hero image" value={about.hero} onChange={(v) => setAbout("hero", v)} />
        <Field label="Intro"><textarea rows={3} value={about.intro || ""} onChange={(e) => setAbout("intro", e.target.value)} className="adm-input resize-none" /></Field>
        <Field label="Body"><textarea rows={4} value={about.body || ""} onChange={(e) => setAbout("body", e.target.value)} className="adm-input resize-none" /></Field>
        <div className="pt-2 border-t border-white/10" />
        <Field label="RT Sports — title"><input value={about.rt_title || ""} onChange={(e) => setAbout("rt_title", e.target.value)} className="adm-input" /></Field>
        <Field label="RT Sports — tagline"><input value={about.rt_tagline || ""} onChange={(e) => setAbout("rt_tagline", e.target.value)} className="adm-input" /></Field>
        <Field label="RT Sports — paragraph 1"><textarea rows={3} value={about.rt_body || ""} onChange={(e) => setAbout("rt_body", e.target.value)} className="adm-input resize-none" /></Field>
        <Field label="RT Sports — paragraph 2"><textarea rows={3} value={about.rt_body2 || ""} onChange={(e) => setAbout("rt_body2", e.target.value)} className="adm-input resize-none" /></Field>
        <Field label="RT Sports — services (one per line)"><textarea rows={4} value={(about.rt_services || []).join("\n")} onChange={(e) => setAbout("rt_services", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} className="adm-input resize-none" /></Field>
        <ImageField label="RT Sports image" value={about.rt_image} onChange={(v) => setAbout("rt_image", v)} />
      </Section>

      <Section title="Page Banners">
        <ImageField label="Partners page banner" hint="(wide hero background)" value={banners.partners} onChange={(v) => setBanner("partners", v)} aspect="h-24 w-40" />
      </Section>

      <div className="sticky bottom-4 z-10">
        <button onClick={save} disabled={saving} className="d2-btn w-full py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 shadow-2xl">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Site Settings
        </button>
      </div>
    </div>
  );
}

/* ---------- Stats editor ---------- */
function StatsField({ value = [], onChange }) {
  const setAt = (i, k, v) => onChange(value.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...(value || []), { value: "", label: "" }]);
  return (
    <div>
      <label className="lbl">Stats <span className="text-white/40">(big number + label)</span></label>
      <div className="space-y-2">
        {value.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={s.value || ""} onChange={(e) => setAt(i, "value", e.target.value)} placeholder="8" className="adm-input w-28" />
            <input value={s.label || ""} onChange={(e) => setAt(i, "label", e.target.value)} placeholder="Courts" className="adm-input flex-1" />
            <button type="button" onClick={() => removeAt(i)} className="text-white/40 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button type="button" onClick={add} className="text-sc-lime hover:text-white inline-flex items-center gap-1 text-sm font-grotesk"><Plus className="w-4 h-4" /> Add stat</button>
      </div>
    </div>
  );
}

/* ---------- Courts Panel ---------- */
export function CourtsPanel({ onLogout }) {
  const { toast } = useToast();
  const [courts, setCourts] = useState([]);
  const [slug, setSlug] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchCourtsAdmin();
      setCourts(data);
      setSlug((cur) => cur || (data[0] && data[0].slug));
    } catch { onLogout && onLogout(); }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const c = courts.find((x) => x.slug === slug);
    if (c) setForm({ ...c });
  }, [slug, courts]);

  if (!form) return <div className="text-white/50 flex items-center gap-2 font-grotesk"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { eyebrow: form.eyebrow, title: form.title, tagline: form.tagline, hero: form.hero, body: form.body, features: form.features, stats: form.stats, gallery: form.gallery };
      const updated = await updateCourt(slug, payload);
      setCourts((cs) => cs.map((c) => c.slug === slug ? updated : c));
      toast({ title: "Saved", description: `${form.title} updated.` });
    } catch { toast({ title: "Couldn't save", description: "Please try again." }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex flex-wrap gap-2">
        {courts.map((c) => (
          <button key={c.slug} onClick={() => setSlug(c.slug)}
            className={`px-4 py-2 rounded-lg font-grotesk text-sm uppercase tracking-wide transition-colors ${slug === c.slug ? "bg-sc-lime text-[#06170f]" : "bg-[#0b241a] border border-white/10 text-white/70 hover:text-sc-lime"}`}>
            {c.title}
          </button>
        ))}
      </div>

      <Section title="Details">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Eyebrow"><input value={form.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} className="adm-input" /></Field>
          <Field label="Title"><input value={form.title || ""} onChange={(e) => set("title", e.target.value)} className="adm-input" /></Field>
        </div>
        <Field label="Tagline"><input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="adm-input" /></Field>
        <Field label="Description"><textarea rows={4} value={form.body || ""} onChange={(e) => set("body", e.target.value)} className="adm-input resize-none" /></Field>
        <Field label="Features (one per line)"><textarea rows={4} value={(form.features || []).join("\n")} onChange={(e) => set("features", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} className="adm-input resize-none" /></Field>
        <StatsField value={form.stats || []} onChange={(v) => set("stats", v)} />
      </Section>

      <Section title="Photos">
        <ImageField label="Hero / banner photo" value={form.hero} onChange={(v) => set("hero", v)} aspect="h-24 w-40" />
        <GalleryField label="Gallery" value={form.gallery || []} onChange={(v) => set("gallery", v)} />
      </Section>

      <div className="sticky bottom-4 z-10">
        <button onClick={save} disabled={saving} className="d2-btn w-full py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 shadow-2xl">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save {form.title}
        </button>
      </div>
    </div>
  );
}

/* ---------- Perks editor (title + desc) ---------- */
function PerksField({ value = [], onChange }) {
  const setAt = (i, k, v) => onChange(value.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...(value || []), { title: "", desc: "" }]);
  return (
    <div>
      <label className="lbl">Highlights / Perks</label>
      <div className="space-y-3">
        {value.map((p, i) => (
          <div key={i} className="flex items-start gap-2 bg-[#06170f] border border-white/10 rounded-lg p-3">
            <div className="flex-1 space-y-2">
              <input value={p.title || ""} onChange={(e) => setAt(i, "title", e.target.value)} placeholder="Title" className="adm-input" />
              <input value={p.desc || ""} onChange={(e) => setAt(i, "desc", e.target.value)} placeholder="Short description" className="adm-input" />
            </div>
            <button type="button" onClick={() => removeAt(i)} className="text-white/40 hover:text-red-400 mt-2"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button type="button" onClick={add} className="text-sc-lime hover:text-white inline-flex items-center gap-1 text-sm font-grotesk"><Plus className="w-4 h-4" /> Add perk</button>
      </div>
    </div>
  );
}

/* ---------- Featured Partner Panel ---------- */
export function FeaturedPartnerPanel({ onLogout }) {
  const { toast } = useToast();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getFeaturedPartner().then((d) => setF(d && d.id ? d : { enabled: true, perks: [] })).catch(() => onLogout && onLogout()); }, [onLogout]);
  if (!f) return <div className="text-white/50 flex items-center gap-2 font-grotesk"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await updateFeaturedPartner(f); toast({ title: "Saved", description: "Featured partner updated." }); }
    catch { toast({ title: "Couldn't save", description: "Please try again." }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <Section title="Featured Partner">
        <div className="flex items-center justify-between bg-[#06170f] border border-white/10 rounded-lg px-4 py-3">
          <div>
            <p className="font-grotesk text-white/90 text-sm font-bold">Show this section on the homepage</p>
            <p className="font-grotesk text-white/40 text-xs mt-0.5">Turn off if the court ends the partnership.</p>
          </div>
          <Switch checked={!!f.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="featured-enabled-switch" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Eyebrow"><input value={f.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} className="adm-input" placeholder="Get Rated" /></Field>
          <Field label="Partner name"><input value={f.name || ""} onChange={(e) => set("name", e.target.value)} className="adm-input" placeholder="DUPR" /></Field>
        </div>
        <Field label="Headline"><input value={f.headline || ""} onChange={(e) => set("headline", e.target.value)} className="adm-input" placeholder="DUPR is the future of the game" /></Field>
        <Field label="Body"><textarea rows={4} value={f.body || ""} onChange={(e) => set("body", e.target.value)} className="adm-input resize-none" /></Field>
        <ImageField label="Partner logo (optional)" value={f.logo} onChange={(v) => set("logo", v)} aspect="h-16 w-40" />
      </Section>

      <Section title="Highlights">
        <PerksField value={f.perks || []} onChange={(v) => set("perks", v)} />
      </Section>

      <Section title="Call to action">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Button label"><input value={f.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)} className="adm-input" placeholder="Claim Your Profile" /></Field>
          <Field label="Button link (URL)"><input value={f.cta_url || ""} onChange={(e) => set("cta_url", e.target.value)} className="adm-input" placeholder="https://mydupr.com" /></Field>
        </div>
      </Section>

      <Section title="Video">
        <Field label="Video URL (YouTube)"><input value={f.video_url || ""} onChange={(e) => set("video_url", e.target.value)} className="adm-input" placeholder="https://youtu.be/… (leave blank to hide the video)" /></Field>
        <ImageField label="Video poster image" value={f.poster} onChange={(v) => set("poster", v)} />
      </Section>

      <div className="sticky bottom-4 z-10">
        <button onClick={save} disabled={saving} className="d2-btn w-full py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 shadow-2xl">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Featured Partner
        </button>
      </div>
    </div>
  );
}

export const SettingsIcon = Settings;
export const CourtsIcon = LayoutGrid;
export const FeaturedIcon = Star;
